import React, { useContext } from "react"
import { graphql } from "gatsby"
import { GatsbyImage, getSrc } from "gatsby-plugin-image"

import styled from "@emotion/styled"
import { Video, Box, Text } from "@singita/components"
import { TEMPLATE_TYPES } from "gatsby-singita-theme/src/common/constants"
import { mapperWithFunction } from "../common/utils"
import { VideoContext } from "gatsby-singita-theme/src/context/VideoContext"
import { generateVideoSchema } from 'gatsby-singita-theme/src/utils/schemaUtils'
import { useRegisterSchema } from 'gatsby-singita-theme/src/context/SchemaContext'

const StyledBackground = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
`
const StyledBackgroundImg = styled(Box)`
  width: 100%;
  height: 100%;
  background-image: url(${({ src }) => src});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
`

const VIDEO_MAP = {
  fullUrl: ["fullUrl"],
  previewUrl: ["previewUrl"],
  renderThumbnail: value => {
    return value.thumbnail
      ? () =>
          value.thumbnail.gatsbyImageData ? (
            <StyledBackground image={value.thumbnail.gatsbyImageData} />
          ) : (
            <StyledBackgroundImg src={value.thumbnail} />
          )
      : null
  },
  canPlay: value => value.canPlay ?? true,
  minHeight: ["minHeight"],
  thumbnailUrl: ({ thumbnail }) => {
    if (!thumbnail) return null

    // Handle both cases: thumbnail as parent object or direct gatsbyImageData
    const imageData = thumbnail.gatsbyImageData || thumbnail

    // Try getSrc first
    const srcFromGetSrc = getSrc(imageData)
    if (srcFromGetSrc) return srcFromGetSrc

    // Fallback: manually extract from gatsbyImageData structure
    if (imageData?.images?.fallback?.src) {
      return imageData.images.fallback.src
    }

    // Last resort: extract from sources srcSet
    if (imageData?.images?.sources?.[0]?.srcSet) {
      const srcSet = imageData.images.sources[0].srcSet
      const firstUrl = srcSet.split(',')[0].trim().split(' ')[0]
      return firstUrl
    }

    return null
  },
  headline: ["headline"],
  caption: ["caption"],
  name: ({ headline, caption }) => {
    return headline || caption || undefined
  },
  description: ({ vimeoMeta, caption, headline }) => {
    // Prioritize Vimeo description, then fall back to caption, then headline
    return vimeoMeta?.description || caption || headline || undefined
  },
  uploadDate: ["createdAt"],
}

const VideoWrapper = props => {
  const { containerSx = {}, visualRatio = null } = props
  const { isOpen, setIsOpen } = useContext(VideoContext)
  const videoProps = mapperWithFunction(props, VIDEO_MAP)

  // Generate and register video schema
  const videoSchema = React.useMemo(() => {
    return generateVideoSchema({
      title: videoProps.headline || videoProps.name,
      headline: videoProps.headline,
      description: videoProps.description,
      caption: videoProps.caption,
      embedUrl: videoProps.previewUrl || videoProps.fullUrl,
      thumbnailUrl: videoProps.thumbnailUrl,
      uploadDate: videoProps.uploadDate,
    });
  }, [videoProps]);

  useRegisterSchema(videoSchema);

  return (
    <Box height="100%" sx={containerSx}>
      <Video
        {...videoProps}
        visualRatio={visualRatio}
        isModalOpen={isOpen}
        toggleModal={videoUrl => setIsOpen(videoUrl, true)}
      />
      {props.caption ? (
        <Box mt={[1]} textAlign="center">
          <Text size="tiny">{props.caption}</Text>
        </Box>
      ) : null}
    </Box>
  )
}

export default VideoWrapper

export const VideoFields = graphql`
  fragment VideoFields on ContentfulVideo {
    id
    contentful_id
    caption
    headline
    fullUrl
    previewUrl
    thumbnail {
      gatsbyImageData(
        placeholder: NONE
        layout: CONSTRAINED
        quality: 90
        width: 1000
        formats: [AUTO, WEBP]
      )
    }
    createdAt
    vimeoMeta {
      description
      title
      duration
      width
      height
      authorName
      uploadDate
    }
  }
`